
import * as React from 'react';
import type { useForm, FieldValues, Path } from 'react-hook-form';
import { RadioGroup }    from './radio-group';
import { CheckboxGroup } from './checkbox-group';
import type { FieldConfig } from '../types';
import type { QuestionnaireInput } from '../schema';

type FormCtl       = ReturnType<typeof useForm<QuestionnaireInput>>;
type FieldErrorMap = Partial<Record<keyof QuestionnaireInput, { message?: string }>>;

interface DynamicFieldProps {
  field:    FieldConfig;
  control:  FormCtl['control'];
  register: FormCtl['register'];
  watch:    FormCtl['watch'];
  errors:   FieldErrorMap;
}

/**
 * Conditional-visibility rules.
 *
 * Each entry says: "render `dependent` only when the form's `trigger` field
 * contains/equals one of the listed values." The backend's questionnaire
 * config doesn't ship explicit visibility metadata yet, so we keep this
 * mapping small and local — when backend adds `visible_when`, replace this
 * with a generic evaluator.
 */
type VisibilityRule = {
  dependent:    string;
  trigger:      keyof QuestionnaireInput;
  triggerKind:  'includes' | 'equals';
  triggerValue: string;
};

const VISIBILITY_RULES: ReadonlyArray<VisibilityRule> = [
  // Salaried — describe "any other" income exposure.
  { dependent: 'other_exposure_detail',         trigger: 'income_exposures',        triggerKind: 'includes', triggerValue: 'any_other' },
  // Any branch with `services_required` — describe "any other" service.
  { dependent: 'service_other_detail',          trigger: 'services_required',       triggerKind: 'includes', triggerValue: 'any_other' },
  // Business owner — describe `other` entity structure.
  { dependent: 'entity_structure_other_detail', trigger: 'entity_structure',        triggerKind: 'equals',   triggerValue: 'other' },
  // Others branch — describe `other` customer category.
  { dependent: 'other_customer_category_detail', trigger: 'other_customer_category', triggerKind: 'equals',   triggerValue: 'other' },
];

function shouldRender(field: FieldConfig, watch: FormCtl['watch']): boolean {
  const rule = VISIBILITY_RULES.find((r) => r.dependent === field.name);
  if (!rule) return true;

  const value = watch(rule.trigger);
  if (rule.triggerKind === 'includes') {
    return Array.isArray(value) && (value as string[]).includes(rule.triggerValue);
  }
  return value === rule.triggerValue;
}

export function DynamicField({ field, control, register, watch, errors }: DynamicFieldProps) {
  if (!shouldRender(field, watch)) return null;

  const errMessage = (errors[field.name as keyof QuestionnaireInput] as { message?: string } | undefined)?.message;

  // ── single_select → RadioGroup ──────────────────────────────────────────
  if (field.field_type === 'single_select' && field.options) {
    return (
      <RadioGroup
        label={field.label}
        name={field.name as 'annual_income_range' | 'annual_turnover_range' | 'entity_structure' | 'other_customer_category'}
        options={field.options}
        control={control}
        error={errMessage}
      />
    );
  }

  // ── multi_select → CheckboxGroup ────────────────────────────────────────
  if (field.field_type === 'multi_select' && field.options) {
    return (
      <CheckboxGroup
        label={field.label}
        hint={field.help_text}
        name={field.name as 'income_exposures' | 'services_required'}
        options={field.options}
        control={control}
        noneExclusive={field.none_exclusive}
        error={errMessage}
      />
    );
  }

  // ── text → textarea ─────────────────────────────────────────────────────
  if (field.field_type === 'text') {
    const isInvalid = !!errMessage;
    return (
      <div>
        <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          {field.label}
          {field.required && (
            <span className="ml-1 text-destructive normal-case">*</span>
          )}
          {!field.required && (
            <span className="normal-case ml-1 text-muted-foreground/60">(optional)</span>
          )}
        </label>
        <textarea
          // RHF type complains because Path<QuestionnaireInput> is narrower
          // than `string`. The config-driven name is always one of our schema
          // keys, so the cast is safe at runtime.
          {...register(field.name as Path<FieldValues> as keyof QuestionnaireInput)}
          rows={2}
          maxLength={field.max_length ?? 500}
          placeholder={field.help_text ?? undefined}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          style={{ borderColor: isInvalid ? 'hsl(var(--destructive))' : undefined }}
        />
        {errMessage && <p className="text-xs text-destructive mt-1">{errMessage}</p>}
      </div>
    );
  }

  // Unknown field_type — render nothing rather than crash. (Future-proofing
  // in case the backend ships a new type before the frontend ships support.)
  return null;
}

// React import kept for hooks in future extensions.
void React;
